import React from "react";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  Box,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TextField,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { People } from "@mui/icons-material";

import EnhancedTableHead, { Order } from "./components/EnhancedTableHead";
import userService, { IUser } from "../../../services/user.service";
import TableRowItem from "./components/TableRowItem";
import { logger } from "../../../utils/logger";
import { useAlert } from "../../../context/AlertContext";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (a: any, b: any) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const ONLINE_MS = 2 * 60 * 1000;

const lastActivityMs = (u: IUser) =>
  Math.max(
    u.lastSeenAt ? new Date(u.lastSeenAt).getTime() : 0,
    u.lastLoginAt ? new Date(u.lastLoginAt).getTime() : 0
  );

export default function ManageUsers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setAlert } = useAlert();

  const [users, setUsers] = React.useState<IUser[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [order, setOrder] = React.useState<Order>("desc");
  const [orderBy, setOrderBy] = React.useState<string>("createdAt");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [userToDelete, setUserToDelete] = React.useState<string | null>(null);
  const [activeWindow, setActiveWindow] = React.useState<7 | 30 | null>(null);

  const fetchUsers = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userService.getUsers();
      // Filter only "User" type users
      const regularUsers = data.filter((user) => user.userType === "User");
      setUsers(regularUsers);
      logger.info(`Fetched ${regularUsers.length} users`);
    } catch (err: any) {
      const errorMessage = err.message || t("users.fetchFailed");
      setError(errorMessage);
      logger.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleClick = (id: string) => {
    navigate(`/dashboard/manage-users/${id}/info`);
  };

  const handleBlockToggle = async (userId: string, isBlocked: boolean) => {
    try {
      if (isBlocked) {
        await userService.blockUser(userId);
        setAlert("success", t("users.blocked"));
      } else {
        await userService.unblockUser(userId);
        setAlert("success", t("users.unblocked"));
      }
      fetchUsers(); // Refresh the list
    } catch (err: any) {
      setAlert("error", err.message || t("users.actionFailed"));
      logger.error(`Failed to update user block status: ${err.message}`);
    }
  };

  const handleDeleteClick = (userId: string) => {
    setUserToDelete(userId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      await userService.deleteUser(userToDelete);
      logger.info("User deleted successfully");
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers(); // Refresh the list
    } catch (err: any) {
      logger.error(`Failed to delete user: ${err.message}`);
      setAlert("error", err.message || t("users.failedToDelete"));
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter users based on search query
  const filteredUsers = React.useMemo(() => {
    if (!searchQuery) return users;

    const query = searchQuery.toLowerCase();
    return users.filter(
      (user) =>
        user.firstName?.toLowerCase().includes(query) ||
        user.lastName?.toLowerCase().includes(query) ||
        user.phone?.includes(query) ||
        user.email?.toLowerCase().includes(query)
    );
  }, [users, searchQuery]);

  // Compute presence/activity counts across all search-matching users
  const { onlineCount, windowActiveCount, inactiveCount } = React.useMemo(() => {
    const now = Date.now();
    const online = filteredUsers.filter(
      (u) => u.lastSeenAt && now - new Date(u.lastSeenAt).getTime() < ONLINE_MS
    ).length;
    const windowActive = activeWindow !== null
      ? filteredUsers.filter((u) => lastActivityMs(u) >= now - activeWindow * 24 * 60 * 60 * 1000).length
      : filteredUsers.length;
    return {
      onlineCount: online,
      windowActiveCount: windowActive,
      inactiveCount: filteredUsers.length - windowActive,
    };
  }, [filteredUsers, activeWindow]);

  // Filter to users active within the selected window; null = show all
  const windowFilteredUsers = React.useMemo(() => {
    if (activeWindow === null) return filteredUsers;
    const now = Date.now();
    const windowMs = activeWindow * 24 * 60 * 60 * 1000;
    return filteredUsers.filter((u) => lastActivityMs(u) >= now - windowMs || lastActivityMs(u) === 0);
  }, [filteredUsers, activeWindow]);

  const visibleRows = React.useMemo(
    () =>
      [...windowFilteredUsers]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, windowFilteredUsers]
  );

  if (loading) {
    return (
      <Box
        className="flex justify-center items-center"
        sx={{ height: "400px" }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box className="w-full">
      {/* Header */}
      <Box className="flex justify-between items-center my-5 shadow-md w-full p-2 rounded">
        <Box className="flex items-center gap-4 flex-row">
          <Typography variant="h6" gutterBottom>
            <span className="font-bold">{t("users.title")}</span>
          </Typography>
          <Autocomplete
            freeSolo
            className="w-80"
            size="small"
            value={searchQuery}
            onInputChange={(event, newValue) => setSearchQuery(newValue)}
            options={users.map(
              (user) =>
                `${user.firstName} ${user.lastName} - ${user.phone}`
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("users.searchUsers")}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    type: "search",
                  },
                }}
              />
            )}
          />
        </Box>

        <Box className="flex gap-2 flex-wrap">
          <Chip
            icon={<People />}
            label={t("users.totalCount", { count: filteredUsers.length })}
            color="primary"
            variant="outlined"
          />
          <Chip
            label={t("users.onlineCount", { count: onlineCount })}
            color="success"
            size="small"
          />
          <Chip
            label={t("users.activeCount", { count: windowActiveCount })}
            color="success"
            size="small"
          />
          <Chip
            label={t("users.inactiveCount", { count: inactiveCount })}
            color="error"
            size="small"
          />
          <Chip
            label={t("users.activeWindowDays", { days: 7 })}
            size="small"
            color={activeWindow === 7 ? "primary" : "default"}
            variant={activeWindow === 7 ? "filled" : "outlined"}
            onClick={() => setActiveWindow(7)}
          />
          <Chip
            label={t("users.activeWindowDays", { days: 30 })}
            size="small"
            color={activeWindow === 30 ? "primary" : "default"}
            variant={activeWindow === 30 ? "filled" : "outlined"}
            onClick={() => setActiveWindow(30)}
          />
        </Box>
      </Box>

      {/* Users Table */}
      <Box sx={{ width: "100%", mb: 4 }}>
        <Paper sx={{ width: "100%", mb: 2 }}>
          <TableContainer>
            <Table>
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={windowFilteredUsers.length}
              />
              <TableBody>
                {visibleRows.length > 0 ? (
                  visibleRows.map((user) => (
                    <TableRowItem
                      key={user._id}
                      item={user}
                      handleClick={handleClick}
                      onBlockToggle={handleBlockToggle}
                      onDelete={handleDeleteClick}
                      activeWindow={activeWindow}
                    />
                  ))
                ) : (
                  <TableBody>
                    <tr>
                      <td
                        colSpan={8}
                        style={{ textAlign: "center", padding: "20px" }}
                      >
                        <Typography color="text.secondary">
                          {searchQuery
                            ? t("users.emptySearch")
                            : t("users.empty")}
                        </Typography>
                      </td>
                    </tr>
                  </TableBody>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={windowFilteredUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">{t("users.deleteUser")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("users.deleteConfirm")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>{t("common.cancel")}</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
